import { type AdminCompanySelectDto, CompanyType } from '@@types/api.types';
import { ActiveFilters } from '@components/data-table-filter/components/active-filters';
import { FilterActions } from '@components/data-table-filter/components/filter-actions';
import { FilterSelector } from '@components/data-table-filter/components/filter-selector';
import type {
  Column,
  DataTableFilterActions,
  FilterStrategy,
  FiltersState,
} from '@components/data-table-filter/core/types';
import { CompanyLogoPreview } from '@components/shared/company-logo-preview.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table';
import { cn } from '@lib/utils';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { getCompanyTypeLabel, isResearchType } from '@utils/company.util';
import { format } from 'date-fns';
import {
  ArrowDownWideNarrowIcon,
  ArrowUpDown,
  ArrowUpNarrowWideIcon,
  BuildingIcon,
  FlaskConicalIcon,
  Loader2,
  RadarIcon,
  UsersIcon,
  X,
} from 'lucide-react';
import React from 'react';
import { SearchInputWithTagSuggestions } from './search-input-with-tag-suggestions.component';

type TagOption = {
  label: string;
  value: string;
};

type AdminOrganizationsTableProps = {
  actions: DataTableFilterActions;
  columns: Column<AdminCompanySelectDto>[];
  filters: FiltersState;
  isLoading: boolean;
  onSearchChange: (search: string | null) => void;
  onSelectOrganization: (orgId: string) => void;
  onSortingChange: (sorting: SortingState) => void;
  organizations: AdminCompanySelectDto[];
  search: string | null;
  sorting: SortingState;
  strategy: FilterStrategy;
  tagOptions: TagOption[];
};

const formatDateTime = (value: string | Date | undefined) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'MMM d, yyyy');
  } catch {
    return '—';
  }
};

type SearchFilterPillProps = {
  onClear: () => void;
  value: string;
};

const SearchFilterPill: React.FC<SearchFilterPillProps> = (props) => {
  const { onClear, value } = props;

  return (
    <div className="flex h-7 items-center rounded-2xl border border-border bg-background shadow-xs text-xs">
      <span className="px-2 text-muted-foreground">Search</span>
      <Separator orientation="vertical" />
      <span className="px-2">{value}</span>
      <Separator orientation="vertical" />
      <Button
        className="rounded-none rounded-r-2xl text-xs w-7 h-full"
        onClick={onClear}
        type="button"
        variant="ghost"
      >
        <X className="size-4 -translate-x-0.5" />
      </Button>
    </div>
  );
};

export const AdminOrganizationsTable: React.FC<AdminOrganizationsTableProps> = (props) => {
  const {
    actions,
    columns: filterColumns,
    filters,
    isLoading,
    onSearchChange,
    onSelectOrganization,
    onSortingChange,
    organizations,
    search,
    sorting,
    strategy,
    tagOptions,
  } = props;
  const hasSearchFilter = Boolean(search?.trim());

  const tableColumns = React.useMemo<ColumnDef<AdminCompanySelectDto>[]>(
    () => [
      {
        accessorKey: 'name',
        cell: ({ row }) => {
          const org = row.original;
          const hasLogo = Boolean(org.logoUrl);
          const isDeleted = org.deleted;
          const isResearch = isResearchType(org.type);
          const isIntelligence = org.type === CompanyType.INTELLIGENCE;

          return (
            <div className="flex items-center gap-2">
              <CompanyLogoPreview
                className={cn(
                  'size-8 rounded-lg',
                  hasLogo
                    ? 'bg-gray-100'
                    : isIntelligence
                      ? 'bg-teal-100'
                      : isResearch
                        ? 'bg-purple-100'
                        : 'bg-blue-100',
                )}
                fallback={
                  isIntelligence ? (
                    <RadarIcon className="size-4 text-teal-600" />
                  ) : isResearch ? (
                    <FlaskConicalIcon className="size-4 text-purple-600" />
                  ) : (
                    <BuildingIcon className="size-4 text-blue-600" />
                  )
                }
                logoAlt={`${org.name} logo`}
                logoClassName="rounded-lg"
                logoUrl={org.logoUrl ?? undefined}
              />
              <div className="flex flex-col">
                <div className={cn('text-sm font-medium', isDeleted ? 'text-gray-400 line-through' : 'text-gray-900')}>
                  {org.name}
                </div>
                {org.slug && <span className="text-xs text-gray-500 font-mono">{org.slug}</span>}
              </div>
            </div>
          );
        },
        header: 'Organization',
        sortingFn: 'text',
      },
      {
        accessorKey: 'type',
        cell: ({ row }) => {
          const { isParentOrganization, parentOrganizationId, type } = row.original;
          return (
            <div className="flex flex-wrap items-center gap-1">
              {isParentOrganization && (
                <Badge
                  size="xs"
                  variant="secondary"
                >
                  Parent
                </Badge>
              )}
              {parentOrganizationId && (
                <Badge
                  size="xs"
                  variant="secondary"
                >
                  Child
                </Badge>
              )}
              <Badge
                size="xs"
                variant="secondary"
              >
                {getCompanyTypeLabel(type)}
              </Badge>
            </div>
          );
        },
        header: 'Type',
        sortingFn: 'text',
      },
      {
        accessorKey: 'userCount',
        cell: ({ row }) => (
          <div className="flex items-center gap-1 text-xs text-gray-600">
            <UsersIcon className="size-3.5" />
            <span>{row.original.userCount}</span>
          </div>
        ),
        header: 'Users',
        sortingFn: 'basic',
      },
      {
        accessorKey: 'tags',
        cell: ({ row }) => {
          const org = row.original;
          const implicitTags = org.implicitTags ?? [];
          const customTags = org.companyTags ?? [];
          const allTags = [
            ...implicitTags.map((t) => ({ ...t, isImplicit: true })),
            ...customTags.map((t) => ({ ...t, isImplicit: false })),
          ];

          if (allTags.length === 0) return <span className="text-xs text-gray-400">—</span>;

          return (
            <div className="flex flex-wrap gap-1">
              {allTags.slice(0, 3).map((tag, idx) => (
                <Badge
                  key={`${tag.name}-${idx}`}
                  size="xs"
                  variant={tag.isImplicit ? 'secondary' : 'innovator'}
                >
                  {tag.name}
                </Badge>
              ))}
              {allTags.length > 3 && (
                <Badge
                  size="xs"
                  variant="innovator"
                >
                  +{allTags.length - 3}
                </Badge>
              )}
            </div>
          );
        },
        header: 'Tags',
      },
      {
        accessorKey: 'createdAt',
        cell: ({ getValue }) => <span className="text-xs text-gray-600">{formatDateTime(getValue<string>())}</span>,
        header: 'Created',
        sortingFn: 'datetime',
      },
    ],
    [],
  );

  const table = useReactTable({
    columns: tableColumns,
    data: organizations,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange,
    state: { sorting },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <FilterSelector
            actions={actions}
            columns={filterColumns}
            filters={filters}
            strategy={strategy}
          />
          <SearchInputWithTagSuggestions
            actions={actions}
            columns={filterColumns}
            onChange={onSearchChange}
            tagOptions={tagOptions}
            value={search}
          />
        </div>
        {(filters.length > 0 || hasSearchFilter) && (
          <div className="flex w-full items-start justify-between gap-2">
            <div className="flex md:flex-wrap gap-2 w-full flex-1">
              {hasSearchFilter && (
                <SearchFilterPill
                  onClear={() => onSearchChange(null)}
                  value={search ?? ''}
                />
              )}
              <ActiveFilters
                actions={actions}
                columns={filterColumns}
                filters={filters}
                strategy={strategy}
              />
            </div>
            <FilterActions
              actions={actions}
              hasFilters={filters.length > 0}
            />
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center h-48 rounded-xl border">
          <Loader2 className="size-6 animate-spin text-gray-500" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    if (header.isPlaceholder) {
                      return <TableHead key={header.id} />;
                    }

                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();

                    return (
                      <TableHead
                        key={header.id}
                        className="text-xs font-semibold text-gray-500"
                      >
                        {canSort ? (
                          <button
                            className="flex items-center gap-1 text-xs font-semibold text-gray-600"
                            onClick={header.column.getToggleSortingHandler()}
                            type="button"
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {sorted === 'asc' && <ArrowUpNarrowWideIcon className="size-3.5" />}
                            {sorted === 'desc' && <ArrowDownWideNarrowIcon className="size-3.5" />}
                            {!sorted && <ArrowUpDown className="size-3.5 text-gray-400" />}
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className={cn(
                      'cursor-pointer hover:bg-gray-50',
                      row.original.deleted && 'bg-red-50/50 hover:bg-red-50',
                    )}
                    onClick={() => onSelectOrganization(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    className="h-24 text-center text-sm text-gray-500"
                    colSpan={tableColumns.length}
                  >
                    No organizations found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
